import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { theme, sectionTitleStyle, sectionIconWrap, sectionIconPaths } from '../../theme';

const SectionIcon = ({ name }) => (
  <span style={sectionIconWrap}>
    <svg width={17} height={17} viewBox="0 0 24 24" fill="none"
      stroke={theme.orange} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: sectionIconPaths[name] || '' }} />
  </span>
);

const BASE = '/api';
function authFetch(url, opts={}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, { ...opts, headers: { ...(opts.headers||{}), ...(token?{Authorization:`Bearer ${token}`}:{}) } });
}

const DOC_LABELS = {
  contrat_voyage: {
    fr: "Copie du contrat de voyage signé avec l'agence",
    en: "Signed travel contract with the agency",
  },
  preuve_paiement: {
    fr: "Preuve de paiement (relevé de compte, ticket CB…) — ⚠ Une facture ne constitue pas une preuve de paiement",
    en: "Proof of payment (bank statement, card receipt…) — ⚠ An invoice is not a proof of payment",
  },
  rib: {
    fr: "RIB",
    en: "Bank account details / IBAN",
  },
  piece_identite: {
    fr: "Copie de pièce d'identité",
    en: "Copy of ID document",
  },
};

function getDocLabel(doc, lang) {
  if (doc.type !== 'custom' && DOC_LABELS[doc.type]) return DOC_LABELS[doc.type][lang] || DOC_LABELS[doc.type].fr;
  return doc.nom || '';
}

const STATUT_STYLE = {
  attente:  { bg:'#F1F5F9', color:'#475569', label:'En attente', labelEn:'Awaiting' },
  uploade:  { bg:'#EFF6FF', color:'#1D4ED8', label:'Déposé',     labelEn:'Uploaded' },
  accepte:  { bg:'#F0FDF4', color:'#065F46', label:'Accepté',    labelEn:'Accepted' },
  refuse:   { bg:'#FEE2E2', color:'#991B1B', label:'Refusé',     labelEn:'Refused'  },
};

export default function DocsDossier({ dossierId, souhaitClient, modesPaiement, onDocsChange }) {
  const { t, lang } = useLanguage();
  const { isVoyageur } = useAuth();

  const [docs, setDocs]                   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [addingDoc, setAddingDoc]         = useState(false);
  const [newDocNom, setNewDocNom]         = useState('');
  const [uploadingId, setUploadingId]     = useState(null);
  const [previewDoc, setPreviewDoc]         = useState(null);
  const [previewUrl, setPreviewUrl]         = useState(null);
  const [refuseDoc, setRefuseDoc]         = useState(null);
  const [refuseComment, setRefuseComment] = useState('');
  const [refuseCategorie, setRefuseCategorie] = useState('');
  const [refuseError, setRefuseError]     = useState('');
  const [refuseSaving, setRefuseSaving]   = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailCopied, setEmailCopied]     = useState(false);
  const [error, setError]                 = useState('');
  const fileInputRef   = useRef(null);
  const activeUploadId = useRef(null);

  async function ouvrirPreview(doc) {
    try {
      const token = localStorage.getItem('arcus_token');
      const res = await fetch(`${BASE}/docs-dossier/${doc.id}/fichier`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { setError('Impossible de charger le fichier'); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      // Révoquer l'ancien URL blob si existant
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setPreviewDoc(doc);
    } catch(e) { setError(e.message); }
  }

  function fermerPreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewDoc(null);
  }

  const initDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await authFetch(`${BASE}/dossiers/${dossierId}/docs/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      setDocs(Array.isArray(data) ? data : []);
      // Notifier le parent pour synchroniser l'état des pièces (blocage soumission voyageur).
      if (onDocsChange) onDocsChange();
    } catch {
      try {
        const res  = await authFetch(`${BASE}/dossiers/${dossierId}/docs`);
        const data = await res.json();
        setDocs(Array.isArray(data) ? data : []);
      } catch(e) { setError(e.message); }
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId, souhaitClient, JSON.stringify(modesPaiement || [])]);

  useEffect(() => { if (dossierId) initDocs(); }, [initDocs]);

  function triggerUpload(docId) {
    activeUploadId.current = docId;
    fileInputRef.current.click();
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file || !activeUploadId.current) return;
    const docId = activeUploadId.current;
    setUploadingId(docId);
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      const token = localStorage.getItem('arcus_token');
      const res = await fetch(`${BASE}/docs-dossier/${docId}/upload`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setDocs(prev => prev.map(d => d.id===docId ? data : d));
      if (onDocsChange) onDocsChange();
    } catch(e) { setError(e.message); }
    finally { setUploadingId(null); activeUploadId.current = null; e.target.value = ''; }
  }

  async function accepter(docId) {
    try {
      const res  = await authFetch(`${BASE}/docs-dossier/${docId}/accepter`, { method:'POST' });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setDocs(prev => prev.map(d => d.id===docId ? data : d));
      if (onDocsChange) onDocsChange();
    } catch(e) { setError(e.message); }
  }

  async function confirmerRefus() {
    if (!refuseCategorie) return;
    setRefuseSaving(true);
    try {
      const res  = await authFetch(`${BASE}/docs-dossier/${refuseDoc.id}/refuser`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ categorie_refus: refuseCategorie, commentaire: refuseComment }),
      });
      const data = await res.json();
      if (data.error) { setRefuseError(data.error); return; }
      setDocs(prev => prev.map(d => d.id===refuseDoc.id ? data : d));
      setRefuseDoc(null); setRefuseComment(''); setRefuseCategorie(''); setRefuseError('');
      if (onDocsChange) onDocsChange();
    } catch(e) { setRefuseError(e.message); }
    finally { setRefuseSaving(false); }
  }

  async function deverrouiller(docId) {
    try {
      const res  = await authFetch(`${BASE}/docs-dossier/${docId}/deverrouiller`, { method:'POST' });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setDocs(prev => prev.map(d => d.id===docId ? data : d));
      if (onDocsChange) onDocsChange();
    } catch(e) { setError(e.message); }
  }

  async function supprimer(docId) {
    if (!window.confirm(t('docs_souscription.delete_confirm'))) return;
    try {
      await authFetch(`${BASE}/docs-dossier/${docId}`, { method:'DELETE' });
      setDocs(prev => prev.filter(d => d.id !== docId));
    } catch(e) { setError(e.message); }
  }

  async function addCustomDoc() {
    if (!newDocNom.trim()) return;
    try {
      const res  = await authFetch(`${BASE}/dossiers/${dossierId}/docs`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ nom: newDocNom.trim() }),
      });
      const data = await res.json();
      setDocs(prev => [...prev, data]);
      setNewDocNom(''); setAddingDoc(false);
    } catch(e) { setError(e.message); }
  }

  function buildEmailBody() {
    const refused = docs.filter(d => d.statut === 'refuse');
    const lines = refused.map(d => {
      const label   = getDocLabel(d, lang);
      const cat     = d.categorie_refus || '—';
      const comment = d.commentaire_refus ? ` — ${d.commentaire_refus}` : '';
      return `• ${label} > ${cat}${comment}`;
    }).join('\n');
    return `Madame, Monsieur,\n\nSuite à l'examen de votre dossier de demande d'indemnisation, nous avons analysé les pièces que vous nous avez transmises. À ce jour, le(s) document(s) suivant(s) a/ont été refusé(s) :\n\n${lines}\n\nNous vous remercions de bien vouloir nous faire parvenir les documents corrigés dans les meilleurs délais.\n\nCordialement,\nL'équipe Arcus Solutions`;
  }

  function copierEmail() {
    navigator.clipboard.writeText(buildEmailBody()).then(() => {
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    });
  }

  const accepted = docs.filter(d => d.statut==='accepte').length;
  const total    = docs.length;
  const pct      = total > 0 ? Math.round((accepted/total)*100) : 0;

  if (loading) return <div style={{padding:12,color:'#94A3B8',fontSize:13}}>Chargement…</div>;

  return (
    <div>
      <input type="file" ref={fileInputRef} style={{display:'none'}}
        accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
        onChange={handleFileChange}/>

      {/* ── Modal Refus ────────────────────────────────────────── */}
      {refuseDoc && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',
          alignItems:'center',justifyContent:'center',zIndex:1100}}>
          <div style={{background:'#fff',borderRadius:12,padding:24,width:460,maxWidth:'92vw'}}>
            <div style={{fontSize:15,fontWeight:700,marginBottom:4,color:'#DC2626'}}>
              {t('docs_souscription.refuse_title')}
            </div>
            <div style={{fontSize:13,color:'#64748B',marginBottom:16}}>
              <strong>{getDocLabel(refuseDoc, lang)}</strong>
            </div>
            <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>
              {t('docs_souscription.refuse_category')}
            </label>
            <select value={refuseCategorie} onChange={e=>setRefuseCategorie(e.target.value)} autoFocus
              style={{border:`1.5px solid ${refuseCategorie?'#E2E8F0':'#FCA5A5'}`,borderRadius:7,
                padding:'8px 10px',width:'100%',fontSize:13,boxSizing:'border-box',
                fontFamily:'inherit',marginBottom:14,background:'#fff'}}>
              <option value="">{t('docs_souscription.refuse_category_placeholder')}</option>
              {['Document illisible','Document non conforme','Document incomplet'].map(c=>(
                <option key={c}>{c}</option>
              ))}
            </select>
            <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>
              {t('docs_souscription.refuse_detail')}
            </label>
            <textarea value={refuseComment} onChange={e=>setRefuseComment(e.target.value)}
              placeholder={t('docs_souscription.refuse_placeholder')} rows={3}
              style={{border:'0.5px solid #E6E9ED',borderRadius:6,padding:'8px 10px',width:'100%',
                fontSize:13,boxSizing:'border-box',resize:'vertical',fontFamily:'inherit',marginBottom:16}}/>
            {refuseError && (
              <div style={{background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:6,
                padding:'8px 12px',fontSize:12,color:'#991B1B',marginBottom:12}}>
                ⚠ {refuseError}
              </div>
            )}
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <button onClick={()=>{setRefuseDoc(null);setRefuseComment('');setRefuseCategorie('');setRefuseError('');}}
                style={{fontSize:14,padding:'7px 16px',borderRadius:6,border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff'}}>
                {t('common.cancel')}
              </button>
              <button onClick={confirmerRefus} disabled={!refuseCategorie||refuseSaving}
                style={{fontSize:14,padding:'7px 16px',borderRadius:6,background:'#DC2626',color:'#fff',
                  border:'none',cursor:'pointer',fontWeight:700,opacity:(!refuseCategorie||refuseSaving)?.5:1}}>
                {refuseSaving ? t('common.saving') : t('docs_souscription.refuse_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Preview ──────────────────────────────────────── */}
      {previewDoc && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.75)',display:'flex',
          alignItems:'center',justifyContent:'center',zIndex:1100}}
          onClick={fermerPreview}>
          <div style={{background:'#fff',borderRadius:12,overflow:'hidden',maxWidth:'85vw',maxHeight:'85vh',
            display:'flex',flexDirection:'column'}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:'12px 16px',borderBottom:'0.5px solid #E6E9ED',display:'flex',
              alignItems:'center',justifyContent:'space-between'}}>
              <span style={{fontSize:13,fontWeight:700}}>{previewDoc.fichier_nom}</span>
              <button onClick={()=>setPreviewDoc(null)}
                style={{background:'none',border:'none',cursor:'pointer',fontSize:20,color:'#64748B'}} onClick={fermerPreview}>✕</button>
            </div>
            <div style={{flex:1,overflow:'auto',minHeight:300}}>
              {previewDoc.fichier_mime?.startsWith('image/') ? (
                <img src={previewUrl}
                  alt={previewDoc.fichier_nom} style={{maxWidth:'100%',maxHeight:'80vh'}}/>
              ) : previewDoc.fichier_mime === 'application/pdf' ? (
                <iframe src={previewUrl}
                  style={{width:'75vw',height:'75vh',border:'none'}} title={previewDoc.fichier_nom}/>
              ) : (
                <div style={{padding:32,textAlign:'center'}}>
                  <a href={previewUrl} download={previewDoc.fichier_nom}
                    style={{color:'#E8671A',fontWeight:700,fontSize:14}}>
                    ⬇ Télécharger {previewDoc.fichier_nom}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Email ────────────────────────────────────────── */}
      {showEmailModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',
          alignItems:'center',justifyContent:'center',zIndex:1100}}>
          <div style={{background:'#fff',borderRadius:12,padding:24,width:580,maxWidth:'92vw',
            maxHeight:'80vh',display:'flex',flexDirection:'column'}}>
            <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>
              {t('docs_souscription.refuse_email_title')}
            </div>
            <div style={{fontSize:12,color:'#64748B',marginBottom:12}}>
              Objet : <strong>{t('docs_souscription.refuse_email_subject')}</strong>
            </div>
            <pre style={{flex:1,overflowY:'auto',background:'#F8FAFC',border:'0.5px solid #E6E9ED',
              borderRadius:7,padding:14,fontSize:12.5,whiteSpace:'pre-wrap',wordBreak:'break-word',
              fontFamily:'inherit',lineHeight:1.7,marginBottom:14}}>
              {buildEmailBody()}
            </pre>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={()=>setShowEmailModal(false)}
                style={{fontSize:14,padding:'7px 16px',borderRadius:6,border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff'}}>
                {t('common.close')}
              </button>
              <button onClick={copierEmail}
                style={{fontSize:14,padding:'7px 16px',borderRadius:6,border:'1px solid #CBD5E1',
                  cursor:'pointer',background:emailCopied?'#D1FAE5':'#fff',
                  color:emailCopied?'#065F46':'#1A1A1A',fontWeight:600}}>
                {emailCopied ? t('docs_souscription.refuse_email_copied') : t('docs_souscription.refuse_email_copy')}
              </button>
              <button onClick={()=>window.open(`mailto:?subject=${encodeURIComponent(t('docs_souscription.refuse_email_subject'))}&body=${encodeURIComponent(buildEmailBody())}`,'_blank')}
                style={{fontSize:14,padding:'7px 16px',borderRadius:6,background:'#E8671A',
                  color:'#fff',border:'none',cursor:'pointer',fontWeight:700}}>
                {t('docs_souscription.refuse_email_open')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* En-tête + barre de progression */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
        <div style={sectionTitleStyle}><SectionIcon name="pieces" />{t('sinistres.pieces_title')}</div>
        <div style={{fontSize:12,color:'#64748B'}}>{accepted}/{total} {t('docs_souscription.accepted_label')}</div>
      </div>
      <div style={{height:5,background:'#E2E8F0',borderRadius:99,marginBottom:14,overflow:'hidden'}}>
        <div style={{height:'100%',background:'#059669',borderRadius:99,width:`${pct}%`,transition:'width .4s'}}/>
      </div>

      {error && (
        <div style={{background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:7,
          padding:'8px 12px',marginBottom:12,fontSize:13,color:'#991B1B'}}>⚠ {error}</div>
      )}

      {/* Liste des documents */}
      <div style={{border:'0.5px solid #E6E9ED',borderRadius:10,overflow:'hidden',marginBottom:12}}>
        {docs.map((doc, idx) => {
          const st        = STATUT_STYLE[doc.statut] || STATUT_STYLE.attente;
          const stLabel   = lang==='en' ? st.labelEn : st.label;
          const isAccepted = doc.statut === 'accepte';
          const isRefused  = doc.statut === 'refuse';
          const isUploaded = doc.statut === 'uploade';

          return (
            <div key={doc.id} style={{borderBottom: idx<docs.length-1?'1px solid #F1F5F9':'none',
              borderRadius: idx===0?'10px 10px 0 0': idx===docs.length-1?'0 0 10px 10px':'0',
              background: isAccepted?'#F0FDF4':isRefused?'#FFF5F5':'#fff'}}>

              {/* Ligne principale */}
              <div style={{display:'flex',alignItems:'center',padding:'12px 14px',gap:10}}>
                <div style={{width:9,height:9,borderRadius:'50%',flexShrink:0,
                  background: isAccepted?'#059669':isRefused?'#EF4444':isUploaded?'#3B82F6':'#D1D5DB'}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,color:'#1A1A1A',
                    overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {getDocLabel(doc, lang)}
                  </div>
                  <div style={{display:'flex',gap:6,marginTop:3,alignItems:'center',flexWrap:'wrap'}}>
                    {doc.requis && !isAccepted && (
                      <span style={{fontSize:10,background:'#FEF3C7',color:'#92400E',borderRadius:99,
                        padding:'1px 6px',fontWeight:700}}>
                        {t('docs_souscription.required_label')}
                      </span>
                    )}
                    <span style={{fontSize:11,background:st.bg,color:st.color,borderRadius:99,
                      padding:'1px 8px',fontWeight:600}}>
                      {doc.statut ? stLabel : t('docs_souscription.status_attente')}
                    </span>
                    {doc.fichier_nom && (
                      <span style={{fontSize:11,color:'#94A3B8',overflow:'hidden',textOverflow:'ellipsis'}}>
                        📎 {doc.fichier_nom} {doc.fichier_taille?`(${Math.round(doc.fichier_taille/1024)} Ko)`:''} 
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div style={{display:'flex',gap:6,flexShrink:0}}>
                  {/* Upload */}
                  {!isAccepted && (
                    <button onClick={()=>triggerUpload(doc.id)} disabled={uploadingId===doc.id}
                      title={t('docs_souscription.upload_btn')}
                      style={{background:'#F8FAFC',border:'0.5px solid #E6E9ED',borderRadius:6,
                        padding:'5px 8px',cursor:'pointer',fontSize:13,opacity:uploadingId===doc.id?.5:1}}>
                      {uploadingId===doc.id ? '…' : '⬆'}
                    </button>
                  )}
                  {/* Prévisualiser */}
                  {doc.fichier_chemin && (
                    <button onClick={()=>ouvrirPreview(doc)} title={t('docs_souscription.preview_btn')}
                      style={{background:'#F8FAFC',border:'0.5px solid #E6E9ED',borderRadius:6,padding:'5px 8px',cursor:'pointer',fontSize:13}}>
                      🔍
                    </button>
                  )}
                  {/* Accepter */}
                  {isUploaded && !isVoyageur && (
                    <button onClick={()=>accepter(doc.id)} title={t('docs_souscription.accept_btn')}
                      style={{background:'#D1FAE5',border:'1px solid #6EE7B7',borderRadius:6,
                        padding:'5px 8px',cursor:'pointer',fontSize:13,color:'#065F46'}}>
                      ✓
                    </button>
                  )}
                  {/* Refuser */}
                  {isUploaded && !isVoyageur && (
                    <button onClick={()=>{setRefuseDoc(doc);setRefuseComment('');setRefuseCategorie('');setRefuseError('');}}
                      title={t('docs_souscription.refuse_btn')}
                      style={{background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:6,
                        padding:'5px 8px',cursor:'pointer',fontSize:13,color:'#DC2626'}}>
                      ✕
                    </button>
                  )}
                  {/* Déverrouiller */}
                  {isAccepted && !isVoyageur && (
                    <button onClick={()=>deverrouiller(doc.id)} title={t('docs_souscription.unlock_btn')}
                      style={{background:'#F8FAFC',border:`0.5px solid ${theme.border}`,borderRadius:6,
                        padding:'5px 8px',cursor:'pointer',fontSize:13,display:'inline-flex',alignItems:'center'}}>
                      <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke={theme.textSecondary} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
                        dangerouslySetInnerHTML={{ __html: sectionIconPaths.unlock }} />
                    </button>
                  )}
                  {/* Supprimer */}
                  {!isAccepted && !isVoyageur && (
                    <button onClick={()=>supprimer(doc.id)} title={t('docs_souscription.delete_btn')}
                      style={{background:'#FFF5F5',border:'1px solid #FCA5A5',borderRadius:6,
                        padding:'5px 8px',cursor:'pointer',fontSize:13,color:'#DC2626'}}>
                      🗑
                    </button>
                  )}
                </div>
              </div>

              {/* Motif refus */}
              {isRefused && (
                <div style={{margin:'0 14px 12px 33px',background:'#FFF5F5',border:'1px solid #FCA5A5',
                  borderRadius:6,padding:'8px 12px',fontSize:12.5,color:'#7F1D1D',
                  display:'flex',flexDirection:'column',gap:4}}>
                  {doc.categorie_refus
                    ? <span style={{display:'inline-block',background:'#FEE2E2',color:'#991B1B',
                        borderRadius:99,padding:'2px 10px',fontSize:11,fontWeight:700,width:'fit-content'}}>
                        ✕ {doc.categorie_refus}
                      </span>
                    : <span style={{fontWeight:700,color:'#991B1B'}}>✕ Document refusé</span>
                  }
                  {doc.commentaire_refus && (
                    <div style={{fontSize:12,color:'#7F1D1D'}}>
                      <strong>{t('docs_souscription.rejected_comment')}</strong> {doc.commentaire_refus}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {docs.length===0 && (
          <div style={{padding:24,textAlign:'center',color:'#94A3B8',fontSize:13}}>
            {t('common.no_results')}
          </div>
        )}
      </div>

      {/* Bouton email si refus — masqué pour le voyageur */}
      {!isVoyageur && docs.some(d=>d.statut==='refuse') && (
        <div style={{marginBottom:12}}>
          <button onClick={()=>setShowEmailModal(true)}
            style={{width:'100%',padding:'10px',background:'#FFF7F2',border:'1px solid #FED7AA',
              borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:700,color:'#C2410C',
              display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
            {t('docs_souscription.refuse_email_btn')}
          </button>
        </div>
      )}

      {/* Ajouter un document — masqué voyageur */}
      {!isVoyageur && <div style={{marginTop:8}}>
        {!addingDoc ? (
          <button onClick={()=>setAddingDoc(true)}
            style={{fontSize:14,color:'#2563EB',background:'none',border:'1px dashed #93C5FD',
              borderRadius:7,padding:'9px 16px',cursor:'pointer',width:'100%',fontWeight:600}}>
            {t('docs_souscription.add_doc')}
          </button>
        ) : (
          <div style={{background:'#F8FAFC',border:'0.5px solid #E6E9ED',borderRadius:8,padding:14}}>
            <label style={{fontSize:12,color:'#64748B',display:'block',marginBottom:5,fontWeight:700}}>
              {t('docs_souscription.add_doc_label')}
            </label>
            <div style={{display:'flex',gap:8}}>
              <input value={newDocNom} onChange={e=>setNewDocNom(e.target.value)}
                placeholder={t('docs_souscription.add_doc_placeholder')}
                onKeyDown={e=>e.key==='Enter'&&addCustomDoc()}
                style={{flex:1,border:'0.5px solid #E6E9ED',borderRadius:6,padding:'7px 10px',
                  fontSize:13,fontFamily:'inherit'}}/>
              <button onClick={addCustomDoc} disabled={!newDocNom.trim()}
                style={{background:'#E8671A',color:'#fff',border:'none',borderRadius:6,
                  padding:'7px 14px',cursor:'pointer',fontWeight:700,fontSize:13,
                  opacity:!newDocNom.trim()?.5:1}}>
                {t('common.add')}
              </button>
              <button onClick={()=>{setAddingDoc(false);setNewDocNom('');}}
                style={{background:'#F1F5F9',color:'#475569',border:'none',borderRadius:6,
                  padding:'7px 10px',cursor:'pointer',fontSize:13}}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>}
    </div>
  );
}
